import { Trans, useLingui } from "@lingui/react/macro";
import {
  Alert,
  Button,
  Group,
  NumberInput,
  rem,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { TimeInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { IconInfoCircle } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

import {
  getGetBookingQueryKey,
  useChangeBookingStatus,
  useCreateInvoicedraftinPowerOffice,
} from "@/shared/api/generated/booking";
import {
  getGetCustomerbyCustomerNumberQueryOptions,
  useUpdateCustomer,
} from "@/shared/api/generated/customers";
import { GetBookingData } from "@/shared/api/generated/schemas";
import { AnchorLink } from "@/shared/components/AnchorLink";
import { emptyStringToNull } from "@/shared/utils/forms/emptyStringToNull";

export default function SendBookingForm({
  booking,
  close,
}: {
  booking: GetBookingData;
  close: () => void;
}) {
  const { t } = useLingui();

  const { museumId } = useParams({
    from: "/_app/(single-location)/museum/$museumId",
  });

  const { mutateAsync: createInvoiceDraft } =
    useCreateInvoicedraftinPowerOffice();
  const { mutateAsync: updateCustomer } = useUpdateCustomer();

  const { mutateAsync: changeBookingStatus } = useChangeBookingStatus();

  const products = booking.program?.filter((p) => p.productID !== null) ?? [];

  const powerOfficeCustomerId = booking.customer.powerOfficeCustomerId;
  const hasPowerOfficeCustomerId = !!powerOfficeCustomerId;

  const form = useForm({
    initialValues: {
      powerOfficeId: powerOfficeCustomerId ?? "",
    },
    initialErrors: {
      powerOfficeId: !hasPowerOfficeCustomerId ? t`Mangler ID` : undefined,
    },
  });

  const queryClient = useQueryClient();

  async function handleSubmit(values: typeof form.values) {
    if (booking.customer.powerOfficeCustomerId !== values.powerOfficeId) {
      const fullCustomerData = await queryClient.fetchQuery(
        getGetCustomerbyCustomerNumberQueryOptions(booking.customer.id),
      );
      await updateCustomer({
        id: booking.customer.id,
        data: {
          ...fullCustomerData.data,
          powerofficeCustomerId: emptyStringToNull(values.powerOfficeId),
        },
      });
    }

    await createInvoiceDraft({ id: booking.id.toString() });
    await changeBookingStatus({
      id: booking.id,
      data: {
        status: "sentforinvoicing",
        updatedAt: booking.updatedAt,
      },
    });
    await queryClient.invalidateQueries({
      queryKey: getGetBookingQueryKey(booking.id),
    });
    close();
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <Text size="sm" c="dimmed">
          <Trans>
            Når du sender en booking til fakturering, overføres kundenavn og
            produkter til PowerOffice. Husk å sjekke at alt stemmer - bookingen
            kan ikke endres etterpå.
          </Trans>
        </Text>

        {!hasPowerOfficeCustomerId ? (
          <Alert
            color="orange"
            title={<Trans>Kunde mangler PowerOffice-ID</Trans>}
            icon={<IconInfoCircle size={16} />}
          >
            <Trans>
              Denne kunden mangler PowerOffice-ID. Du kan fortsatt sende
              bookingen til fakturering, men kundenavnet blir ikke sendt med.
              Har du riktig ID, kan du legge den inn i feltet under - eller{" "}
              <AnchorLink
                to="/museum/$museumId/customers/$customerId"
                params={{
                  museumId: museumId,
                  customerId: booking.customer.id.toString(),
                }}
              >
                gå til kundedetaljene
              </AnchorLink>{" "}
              for å legge den til der.
            </Trans>
          </Alert>
        ) : null}

        <Title size="sm">
          <Trans>Oppsummering</Trans>
        </Title>

        <Group grow align="start">
          <Select
            label={t`Kunde`}
            data={[
              {
                label: booking.customer.name,
                value: booking.customer.id.toString(),
              },
            ]}
            value={booking.customer.id.toString()}
            readOnly
          />
          <TextInput
            label={t`PowerOffice-ID`}
            {...form.getInputProps("powerOfficeId")}
            readOnly={hasPowerOfficeCustomerId}
          />
        </Group>

        <Table withRowBorders={false} horizontalSpacing={4} verticalSpacing={4}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ minWidth: rem(150) }}>
                <Trans>Produkt</Trans>
              </Table.Th>
              <Table.Th style={{ width: rem(75) }}>
                <Trans>Start</Trans>
              </Table.Th>
              <Table.Th style={{ width: rem(75) }}>
                <Trans>Slutt</Trans>
              </Table.Th>
              <Table.Th style={{ width: rem(100), minWidth: rem(70) }}>
                <Trans>Pris (inkl. MVA)</Trans>
              </Table.Th>
              <Table.Th style={{ width: rem(90), minWidth: rem(80) }}>
                <Trans>Antall</Trans>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {products.map((product) => (
              <Table.Tr key={product.productID}>
                <Table.Td valign="top">
                  <Select
                    data={[
                      {
                        label: product.productNameNO ?? "",
                        value: product.productID ?? "",
                      },
                    ]}
                    value={product.productID}
                    readOnly
                  />
                </Table.Td>
                <Table.Td valign="top">
                  <TimeInput value={product.start ?? ""} readOnly />
                </Table.Td>
                <Table.Td valign="top">
                  <TimeInput value={product.end ?? ""} readOnly />
                </Table.Td>
                <Table.Td valign="top">
                  <NumberInput
                    value={product.price}
                    hideControls
                    allowDecimal={false}
                    thousandSeparator={" "}
                    styles={{
                      input: { textAlign: "right" },
                    }}
                    readOnly
                  />
                </Table.Td>
                <Table.Td valign="top">
                  <NumberInput value={product.quantity} readOnly />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        <Group justify="end" gap={8} mt={8}>
          <Button variant="subtle" onClick={close}>
            <Trans>Avbryt</Trans>
          </Button>
          <Button type="submit" loading={form.submitting}>
            <Trans>Fakturer</Trans>
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
